import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
} from '@mui/material';
import {
  Lock as LockIcon,
  Upgrade as UpgradeIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';

interface FeatureLockedOverlayProps {
  feature: string;
  requiredPlan: 'premium' | 'pro';
  onUpgrade?: () => void;
  onClose?: () => void;
  showCloseButton?: boolean;
  variant?: 'overlay' | 'card';
}

export const FeatureLockedOverlay: React.FC<FeatureLockedOverlayProps> = ({
  feature,
  requiredPlan,
  onUpgrade,
  onClose,
  showCloseButton = false,
  variant = 'overlay',
}) => {
  const { theme } = useTheme();

  const getPlanColor = () => {
    return requiredPlan === 'pro' ? '#F59E0B' : theme.colors.secondary;
  };

  const getPlanName = () => {
    return requiredPlan === 'pro' ? 'Pro' : 'Basic';
  };

  const overlayContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: 3,
        gap: 2,
      }}
    >
      {showCloseButton && onClose && (
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: theme.colors.text.secondary,
          }}
        >
          <CloseIcon />
        </IconButton>
      )}

      <Box
        sx={{
          backgroundColor: getPlanColor(),
          borderRadius: '50%',
          width: 64,
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 1,
        }}
      >
        <LockIcon sx={{ color: 'white', fontSize: 32 }} />
      </Box>

      <Typography
        variant="h6"
        sx={{
          fontWeight: 'bold',
          color: theme.colors.text.primary,
          marginBottom: 0.5,
        }}
      >
        {feature}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: theme.colors.text.secondary,
          marginBottom: 1,
          maxWidth: 280,
        }}
      >
        Esta funcionalidade está disponível apenas no plano {getPlanName()}.
        Faça upgrade para acessar este recurso!
      </Typography>

      {onUpgrade && (
        <Button
          onClick={onUpgrade}
          startIcon={<UpgradeIcon />}
          sx={{
            backgroundColor: getPlanColor(),
            color: 'white',
            borderRadius: theme.radii.pill,
            padding: '12px 24px',
            fontWeight: 'bold',
            textTransform: 'none',
            boxShadow: theme.shadows.medium,
            '&:hover': {
              backgroundColor: getPlanColor(),
              opacity: 0.9,
            },
          }}
        >
          Upgrade para {getPlanName()}
        </Button>
      )}
    </Box>
  );

  if (variant === 'card') {
    return (
      <Paper
        elevation={3}
        sx={{
          borderRadius: theme.radii.large,
          backgroundColor: theme.colors.background,
          border: `2px dashed ${getPlanColor()}`,
          position: 'relative',
        }}
      >
        {overlayContent}
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(4px)',
        borderRadius: 'inherit',
        zIndex: 10,
        cursor: 'not-allowed',
      }}
    >
      {overlayContent}
    </Box>
  );
};
